import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import PanelCard from '../../performance-operations/components/PanelCard';
import {
  Badge, Clickable, Grid, KpiSub, KpiValue, Sub, Table,
} from '../../performance-operations/styles';
import { formatNumber, hasValue, usePanel } from '../../performance-operations/config';
import { formatDateTime } from '../../../utils/Utils';
import { fetchWorkspaceSummary, signedDiff } from './config';

// Analiz E08 "Oturum Özeti" — üst alan: oturum, kapsam n, tamamlanma (M03), önce/sonra ortalaması (M26), değişen oran (M27),
// yukarı/aşağı (M28) ve gerekçe kapsaması (M70). M26–M28 YALNIZ hem önce hem ONAYLI son puanı olan aynı çalışan kümesinde; oturum
// bitmeden öneri ayrı kartta ("Öneri", onaylı değil). Öneri → kurul onayı → dönem final onayı → sonuç yayımı ayrı adımlar ve
// ayrı izinlerdir; adımlar burada görünür. Eksik gerekçe / kanıt sayısı Liste sekmesine süzgeçle gider.
const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Step = styled.li`
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-left: 4px solid ${({ theme, $status }) => ($status === 'done' ? theme.colors.secondary.main
    : $status === 'inProgress' ? theme.colors.primary.main : theme.colors.surface.border)};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

function Kpi({ code, title, value, sub, panel }) {
  return (
    <PanelCard panelKey={`kpi-${code}`} title={title} span={3} kpi compact loading={panel.loading} error={panel.error} onRetry={panel.retry}>
      <KpiValue data-kpi={code}>{value}</KpiValue>
      {sub ? <KpiSub>{sub}</KpiSub> : null}
    </PanelCard>
  );
}

function ChangeTable({ t, lang, stats }) {
  const n = (v, d = 2) => (v == null ? '-' : formatNumber(v, lang, d));
  const pct = (v) => (v == null ? '-' : `${formatNumber(v, lang)}%`);
  return (
    <Table>
      <thead>
        <tr>
          <th>{t('performance.calibrationWorkspace.col.matched')}</th>
          <th>{t('performance.calibrationWorkspace.col.avgBefore')}</th>
          <th>{t('performance.calibrationWorkspace.col.avgAfter')}</th>
          <th>{t('performance.calibrationWorkspace.col.delta')}</th>
          <th>{t('performance.calibrationWorkspace.col.changed')}</th>
          <th>{t('performance.calibrationWorkspace.col.up')}</th>
          <th>{t('performance.calibrationWorkspace.col.down')}</th>
          <th>{t('performance.calibrationWorkspace.col.equal')}</th>
        </tr>
      </thead>
      <tbody>
        <tr data-row={stats.mode}>
          <td>{n(stats.matchedN, 0)}</td>
          <td>{n(stats.avgBefore)}</td>
          <td>{n(stats.avgAfter)}</td>
          <td>{signedDiff(stats.delta, (v) => n(v))}</td>
          <td>{`${n(stats.changed, 0)} (${pct(stats.changedPct)})`}</td>
          <td>{`${n(stats.up, 0)} (${pct(stats.upPct)})`}</td>
          <td>{`${n(stats.down, 0)} (${pct(stats.downPct)})`}</td>
          <td>{`${n(stats.equal, 0)} (${pct(stats.equalPct)})`}</td>
        </tr>
      </tbody>
    </Table>
  );
}

export default function WorkspaceSummaryTab({ sessionId, refreshKey, onOpenList }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const panel = usePanel(() => fetchWorkspaceSummary(sessionId), [sessionId, refreshKey], !!sessionId);
  const s = panel.data;
  const approved = s?.approved;
  const closed = s?.status === 'closed';
  const noData = t('performance.operations.kpi.noData');
  const m70 = s?.justificationCoverage;

  return (
    <Grid data-tab="summary">
      <Kpi
        code="scope" title={t('performance.calibrationWorkspace.kpi.scope')} panel={panel}
        value={s ? formatNumber(s.scopeN, lang, 0) : noData}
        sub={s ? t('performance.calibrationWorkspace.kpi.proposed', { n: s.proposedN }) : null}
      />
      <Kpi
        code="M03" title={t('performance.calibrationWorkspace.kpi.completion')} panel={panel}
        value={hasValue(s?.completion) ? `${formatNumber(s.completion.value, lang)}%` : noData}
        sub={hasValue(s?.completion) ? t('performance.operations.kpi.ratio', { num: s.completion.numerator, den: s.completion.denominator }) : null}
      />
      <Kpi
        code="M26" title={t('performance.calibrationWorkspace.kpi.beforeAfter')} panel={panel}
        value={approved?.matchedN ? `${formatNumber(approved.avgBefore, lang)} → ${formatNumber(approved.avgAfter, lang)}` : noData}
        sub={approved?.matchedN ? t('performance.calibrationWorkspace.kpi.beforeAfterSub', { n: approved.matchedN, delta: signedDiff(approved.delta, (v) => formatNumber(v, lang)) })
          : t('performance.calibrationWorkspace.kpi.approvedOnly')}
      />
      <Kpi
        code="M70" title={t('performance.calibrationWorkspace.kpi.justification')} panel={panel}
        value={hasValue(m70) ? `${formatNumber(m70.value, lang)}%` : noData}
        sub={s ? t('performance.calibrationWorkspace.kpi.justificationSub', { justified: s.justifiedChanges, evidenced: s.evidencedChanges, total: s.approvedChanges }) : null}
      />

      <PanelCard panelKey="changes" title={t('performance.calibrationWorkspace.panel.changes')} span={8} loading={panel.loading} error={panel.error} onRetry={panel.retry}>
        {approved ? (
          <>
            <Sub>{t('performance.calibrationWorkspace.approvedStats')}</Sub>
            <ChangeTable t={t} lang={lang} stats={approved} />
          </>
        ) : null}
        {s && !closed ? (
          <>
            <Sub data-note="proposal">{t('performance.calibrationWorkspace.proposalStats')}</Sub>
            <ChangeTable t={t} lang={lang} stats={s.proposal} />
          </>
        ) : null}
        <Sub>{t('performance.calibrationWorkspace.epsilonNote')}</Sub>
      </PanelCard>

      <PanelCard panelKey="review" title={t('performance.calibrationWorkspace.panel.review')} span={4} loading={panel.loading} error={panel.error} onRetry={panel.retry}>
        {s ? (
          <Table>
            <tbody>
              {[
                ['reviewed', s.reviewedN, { reviewStatus: 'reviewed' }],
                ['discuss', s.discussN, { reviewStatus: 'discuss' }],
                ['pending', s.pendingReviewN, { reviewStatus: 'pending' }],
                ['missingJustification', s.missingJustificationN, { missingJustification: true }],
                ['missingEvidence', s.missingEvidenceN, null],
              ].map(([k, v, filter]) => (
                <tr key={k} data-row={k}>
                  <td>
                    {filter && v > 0
                      ? <Clickable type="button" data-open-list={k} onClick={() => onOpenList(filter)}>{t(`performance.calibrationWorkspace.review.${k}`)}</Clickable>
                      : t(`performance.calibrationWorkspace.review.${k}`)}
                  </td>
                  <td>{formatNumber(v ?? 0, lang, 0)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
        <Sub>{t('performance.calibrationWorkspace.aiNote')}</Sub>
      </PanelCard>

      <PanelCard panelKey="pipeline" title={t('performance.calibrationWorkspace.panel.pipeline')} span={12} loading={panel.loading} error={panel.error} onRetry={panel.retry}>
        <Steps>
          {(s?.pipeline || []).map((step) => (
            <Step key={step.key} $status={step.status} data-step={step.key} data-status={step.status}>
              <strong>{t(`performance.calibrationWorkspace.step.${step.key}`)}</strong>
              <div>
                <Badge $tone={step.status === 'done' ? 'success' : step.status === 'inProgress' ? 'info' : undefined}>
                  {t(`performance.calibrationWorkspace.stepStatus.${step.status}`)}
                </Badge>
              </div>
              {step.total != null ? <Sub>{t('performance.operations.kpi.ratio', { num: step.done ?? 0, den: step.total })}</Sub> : null}
              {step.at ? <Sub>{formatDateTime(step.at, lang)}</Sub> : null}
              {step.byName ? <Sub>{step.byName}</Sub> : null}
            </Step>
          ))}
        </Steps>
        <Sub>{t('performance.calibrationWorkspace.pipelineNote')}</Sub>
      </PanelCard>
    </Grid>
  );
}
